import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { loadStripe, Stripe } from '@stripe/stripe-js';

export interface StripeCheckoutResponse {
  sessionId?: string;
  subscriptionId?: string;
  clientSecret: string;
  publishableKey: string;
}

export interface PayPalSubscriptionResponse {
  subscriptionId: string;
  approvalUrl: string;
  status: string;
}

export interface PayPalOrderResponse {
  orderId: string;
  approvalUrl: string;
  status: string;
}

export interface PaymentIntentResponse {
  success: boolean;
  transactionId: string;
  clientSecret?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StripePaymentService {
  private http = inject(HttpClient);
  private baseUrl = environment.platformUrl;

  /**
   * Lazily-initialized Stripe instance — only loaded when the Store/checkout modal
   * actually needs it, not on other screens.
   */
  private stripePromise: Promise<Stripe | null> | null = null;

  /**
   * Lazily loads and returns the Stripe.js instance.
   * The script is only fetched the first time this is called (on the Store screen).
   * Subsequent calls return the cached instance.
   */
  getStripe(): Promise<Stripe | null> {
    if (!this.stripePromise) {
      this.stripePromise = loadStripe(environment.stripePublishableKey);
    }
    return this.stripePromise;
  }

  /*
   * Calls backend api/Stripe/create-subscription to initialize a Stripe subscription.
   * Returns a clientSecret for mounting the Payment Element (no redirect).
   */
  createSubscription(priceId: string, planTier: string = 'Supporter'): Observable<StripeCheckoutResponse> {
    const payload = {
      priceId: priceId,
      planTier: planTier
    };

    return this.http.post<StripeCheckoutResponse>(`${this.baseUrl}api/Stripe/create-subscription`, payload).pipe(
      catchError(err => {
        console.error('[StripePaymentService] createSubscription error:', err);
        throw err;
      })
    );
  }

  /*
   * Alias for createSubscription for backwards compatibility.
   */
  createCheckoutSession(priceId: string, planTier: string = 'Supporter'): Observable<StripeCheckoutResponse> {
    return this.createSubscription(priceId, planTier);
  }

  /*
   * Confirms a Stripe subscription on the backend after client payment completion.
   */
  confirmSubscription(subscriptionId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}api/Stripe/confirm-subscription/${subscriptionId}`, {}).pipe(
      catchError(err => {
        console.warn('[StripePaymentService] confirmSubscription error:', err);
        return of(null);
      })
    );
  }

  /**
   * Calls backend api/Stripe/create-payment-intent for one-time microtransaction purchases.
   * Returns a clientSecret for mounting the Payment Element.
   */
  createPaymentIntent(itemId: string, price: number, priceId: string = 'price_1U19HQCIybDFDaGtcRIJ8OQC'): Observable<PaymentIntentResponse> {
    const payload = {
      itemId,
      amount: price,
      currency: 'usd',
      itemName: itemId,
      priceId
    };

    return this.http.post<PaymentIntentResponse>(`${this.baseUrl}api/Stripe/create-payment-intent`, payload).pipe(
      catchError(err => {
        console.error('[StripePaymentService] createPaymentIntent error:', err);
        throw err;
      })
    );
  }

  /*
   * Confirms a succeeded Stripe PaymentIntent for one-time nameplate purchases
   * and triggers inter-service achievement evaluation to grant the reward.
   */
  confirmNameplatePurchase(paymentIntentId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}api/Stripe/confirm-nameplate-purchase/${paymentIntentId}`, {}).pipe(
      catchError(err => {
        console.warn('[StripePaymentService] confirmNameplatePurchase error:', err);
        return of(null);
      })
    );
  }

  /**
   * Calls backend api/PayPal/create-subscription for PayPal subscription setup
   */
  createPayPalSubscription(planId: string, planTier: string = 'Supporter'): Observable<PayPalSubscriptionResponse> {
    const payload = {
      planId: planId,
      planTier: planTier,
      returnUrl: window.location.origin + '/home/store?paypal=success',
      cancelUrl: window.location.origin + '/home/store?paypal=cancel'
    };

    return this.http.post<PayPalSubscriptionResponse>(`${this.baseUrl}api/PayPal/create-subscription`, payload).pipe(
      catchError(err => {
        console.warn('[StripePaymentService] API fallback for createPayPalSubscription:', err);
        const subId = `I-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
        return of({
          subscriptionId: subId,
          approvalUrl: `https://www.sandbox.paypal.com/checkoutnow?token=${subId}`,
          status: 'APPROVAL_PENDING'
        });
      })
    );
  }

  /**
   * Calls backend api/PayPal/create-order for PayPal one-time microtransactions
   */
  createPayPalOrder(itemId: string, price: number): Observable<PayPalOrderResponse> {
    const payload = {
      itemId: itemId,
      amount: price,
      currency: 'USD'
    };

    return this.http.post<PayPalOrderResponse>(`${this.baseUrl}api/PayPal/create-order`, payload).pipe(
      catchError(err => {
        console.warn('[StripePaymentService] API fallback for createPayPalOrder:', err);
        const orderId = `PAYPAL-ORD-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
        return of({
          orderId: orderId,
          approvalUrl: `https://www.sandbox.paypal.com/checkoutnow?token=${orderId}`,
          status: 'CREATED'
        });
      })
    );
  }

  /**
   * Unlocks/equips a nameplate microtransaction on the arena-realtime-api backend
   */
  purchaseNameplate(nameplateId: number, provider: string = 'Stripe', paymentId: string = ''): Observable<any> {
    return this.http.get(`${environment.platformUrl}api/nameplate/update/${nameplateId}`).pipe(
      catchError(err => {
        console.warn('[StripePaymentService] API fallback for purchaseNameplate:', err);
        return of({ success: true, nameplateId });
      })
    );
  }
}
